import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Table, Pagination, Input, message, Select, Modal } from 'starry-sky-ui';
import type { ColumnType, SelectOption } from 'starry-sky-ui';
import { getArticleList, articleManageAPI } from '@/service/article';
import useTagsManage from '@/hooks/useTagsManage';
import type { ArticlePost } from '@/types';
import './index.scss';

const Articles: React.FC = () => {
  const navigate = useNavigate();

  // ---- 数据 ----
  const [data, setData] = useState<ArticlePost[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);

  // ---- 筛选 ----
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [keyword, setKeyword] = useState('');
  const [tag, setTag] = useState('');
  const [isFeatured, setIsFeatured] = useState<string>('');

  // ---- 批量选择 ----
  const [selectedRowKeys, setSelectedRowKeys] = useState<string[]>([]);

  // ---- 删除确认 ----
  const [deleteTarget, setDeleteTarget] = useState<number | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [batchDeleteLoading, setBatchDeleteLoading] = useState(false);

  // ---- 标签列表 ----
  const { data: tagsList } = useTagsManage();

  // 防抖
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // ---- 获取列表 ----
  const fetchList = useCallback(
    async (p: number, ps: number, kw: string, tg: string, feat: string) => {
      setLoading(true);
      try {
        const params: Record<string, unknown> = { page: p, pageSize: ps };
        if (kw) params.keyword = kw;
        if (tg) params.tag = tg;
        if (feat) params.isFeatured = feat;

        const res = await getArticleList(params);
        if (res.success) {
          setData(res.data.list);
          setTotal(res.data.total);
          setSelectedRowKeys([]);
        }
      } catch {
        message.error('获取文章列表失败');
      } finally {
        setLoading(false);
      }
    },
    []
  );

  // 初始加载
  useEffect(() => {
    fetchList(page, pageSize, keyword, tag, isFeatured);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---- 页码变化 ----
  const handlePageChange = useCallback(
    (p: number, ps: number) => {
      setPage(p);
      setPageSize(ps);
      fetchList(p, ps, keyword, tag, isFeatured);
    },
    [keyword, tag, isFeatured, fetchList]
  );

  // ---- 搜索 ----
  const handleKeywordChange = useCallback(
    (val: string) => {
      setKeyword(val);
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => {
        setPage(1);
        fetchList(1, pageSize, val, tag, isFeatured);
      }, 500);
    },
    [pageSize, tag, isFeatured, fetchList]
  );

  // ---- 筛选变化 ----
  const handleFilterChange = useCallback(
    (field: 'tag' | 'isFeatured', val: string) => {
      if (field === 'tag') setTag(val);
      if (field === 'isFeatured') setIsFeatured(val);
      setPage(1);
      fetchList(
        1,
        pageSize,
        keyword,
        field === 'tag' ? val : tag,
        field === 'isFeatured' ? val : isFeatured
      );
    },
    [pageSize, keyword, tag, isFeatured, fetchList]
  );

  // ---- 编辑 ----
  const handleEdit = useCallback(
    (postsId: number) => {
      navigate(`/admin/write?postsId=${postsId}`);
    },
    [navigate]
  );

  // ---- 单个删除 ----
  const handleDelete = useCallback(async () => {
    if (deleteTarget === null) return;
    setDeleteLoading(true);
    try {
      const res = await articleManageAPI.delete(deleteTarget);
      if (res?.success) {
        message.success('文章已删除');
        setDeleteTarget(null);
        fetchList(page, pageSize, keyword, tag, isFeatured);
      }
    } catch {
      message.error('删除失败');
    } finally {
      setDeleteLoading(false);
    }
  }, [deleteTarget, fetchList, page, pageSize, keyword, tag, isFeatured]);

  // ---- 批量删除 ----
  const handleBatchDelete = useCallback(async () => {
    if (selectedRowKeys.length === 0) return;
    if (!window.confirm(`确定要删除选中的 ${selectedRowKeys.length} 篇文章吗？`)) return;

    setBatchDeleteLoading(true);
    try {
      // 逐个删除（后端若支持批量可优化）
      let successCount = 0;
      for (const id of selectedRowKeys) {
        const res = await articleManageAPI.delete(Number(id));
        if (res?.success) successCount++;
      }
      message.success(`成功删除 ${successCount} 篇文章`);
      setSelectedRowKeys([]);
      fetchList(page, pageSize, keyword, tag, isFeatured);
    } catch {
      message.error('批量删除失败');
    } finally {
      setBatchDeleteLoading(false);
    }
  }, [selectedRowKeys, fetchList, page, pageSize, keyword, tag, isFeatured]);

  // ---- 列定义 ----
  const columns: ColumnType<ArticlePost>[] = [
    {
      title: 'ID',
      dataIndex: 'postsId',
      key: 'postsId',
      width: 60,
    },
    {
      title: '标题',
      dataIndex: 'title',
      key: 'title',
      width: 220,
      render: (val: string) => (
        <span className="articles-title-cell" title={val}>
          {val}
        </span>
      ),
    },
    {
      title: '摘要',
      dataIndex: 'summary',
      key: 'summary',
      render: (val: string) => (
        <span className="articles-summary-cell" title={val}>
          {val}
        </span>
      ),
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 80,
      render: (val: string) => (
        <span
          className={`articles-status-tag ${val === '1' ? 'status-published' : 'status-draft'}`}
        >
          {val === '1' ? '已发布' : '草稿'}
        </span>
      ),
    },
    {
      title: '精选',
      dataIndex: 'is_featured',
      key: 'is_featured',
      width: 70,
      render: (val: string) => (
        <span className={`articles-featured-tag ${val === '1' ? 'featured-yes' : ''}`}>
          {val === '1' ? '★' : '☆'}
        </span>
      ),
    },
    {
      title: '浏览',
      dataIndex: 'view_count',
      key: 'view_count',
      width: 70,
    },
    {
      title: '发布时间',
      dataIndex: 'published_at',
      key: 'published_at',
      width: 160,
      render: (val: string) =>
        val ? val.slice(0, 19).replace('T', ' ') : '—',
    },
    {
      title: '操作',
      key: 'action',
      width: 120,
      render: (_: any, record: ArticlePost) => (
        <div className="articles-actions">
          <button
            className="articles-action-btn"
            onClick={() => handleEdit(record.postsId)}
          >
            编辑
          </button>
          <button
            className="articles-action-btn articles-action-delete"
            onClick={() => setDeleteTarget(record.postsId)}
          >
            删除
          </button>
        </div>
      ),
    },
  ];

  // ---- 标签选项 ----
  const tagOptions: SelectOption[] = [
    { label: '全部标签', value: '' },
    ...(tagsList || []).map((t) => ({ label: t.label, value: t.value })),
  ];

  const featuredOptions: SelectOption[] = [
    { label: '全部', value: '' },
    { label: '精选', value: '1' },
    { label: '非精选', value: '0' },
  ];

  return (
    <div className="articles-page">
      <div className="articles-header">
        <h2 className="articles-title">文章管理</h2>
      </div>

      {/* 筛选栏 */}
      <div className="articles-filters">
        <Input
          placeholder="搜索标题 / 摘要..."
          value={keyword}
          onChange={handleKeywordChange}
          prefix={<span className="articles-search-icon">&#128269;</span>}
          allowClear
          size="middle"
          className="articles-search-input"
        />

        <Select
          options={tagOptions}
          value={tag}
          onChange={(val) => handleFilterChange('tag', String(val))}
          placeholder="标签筛选"
          size="middle"
          style={{ width: 140 }}
        />

        <Select
          options={featuredOptions}
          value={isFeatured}
          onChange={(val) => handleFilterChange('isFeatured', String(val))}
          placeholder="精选筛选"
          size="middle"
          style={{ width: 120 }}
        />

        <span className="articles-count">共 {total} 篇文章</span>
      </div>

      {/* 批量操作栏 */}
      {selectedRowKeys.length > 0 && (
        <div className="articles-batch-bar">
          <span className="batch-info">已选择 {selectedRowKeys.length} 篇文章</span>
          <button
            className="articles-action-btn articles-action-delete"
            onClick={handleBatchDelete}
            disabled={batchDeleteLoading}
          >
            {batchDeleteLoading ? '删除中...' : '批量删除'}
          </button>
        </div>
      )}

      {/* 表格 */}
      <Table<ArticlePost>
        columns={columns}
        dataSource={data}
        rowKey="postsId"
        loading={loading}
        size="middle"
        bordered
        pagination={false}
        rowSelection={{
          selectedRowKeys,
          onChange: (keys) => setSelectedRowKeys(keys),
          getCheckboxProps: () => ({}),
        }}
      />

      {/* 分页 */}
      <div className="articles-pagination-wrap">
        <Pagination
          current={page}
          pageSize={pageSize}
          total={total}
          showTotal
          showSizeChanger
          pageSizeOptions={[10, 20, 30, 50]}
          onChange={handlePageChange}
        />
      </div>

      {/* 删除确认弹窗 */}
      <Modal
        open={deleteTarget !== null}
        title="确认删除"
        onOk={handleDelete}
        onCancel={() => setDeleteTarget(null)}
        onClose={() => setDeleteTarget(null)}
        okText="删除"
        cancelText="取消"
        confirmLoading={deleteLoading}
      >
        <p style={{ color: '#ccc' }}>
          确定要删除 ID 为 <strong>#{deleteTarget}</strong> 的文章吗？此操作不可撤销。
        </p>
      </Modal>
    </div>
  );
};

export default Articles;
